import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Search, Users } from "lucide-react";

const TIER_STYLE = {
  pro: "bg-[#24468C] text-white", lite: "bg-[#E8A93C] text-[#1a3060]",
  trial: "bg-zinc-200 text-zinc-700", trialing: "bg-zinc-200 text-zinc-700",
};

function TierBadge({ tier, status }) {
  const t = tier || "—";
  return <Badge className={`${TIER_STYLE[tier] || "bg-zinc-100 text-zinc-600"} capitalize`} data-testid="user-tier-badge">{t}{status ? ` · ${status}` : ""}</Badge>;
}

function Detail({ userId, onChanged, isSuper }) {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(7);
  const [compTier, setCompTier] = useState("pro");
  const [compMonths, setCompMonths] = useState(1);
  const [role, setRole] = useState("none");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setD(data);
      setRole(data.user.role || "none");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load user."); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed."); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const u = d.user, ent = d.entitlements, sub = d.subscription;

  return (
    <div className="space-y-6" data-testid="user-detail">
      <div>
        <p className="font-heading text-xl font-bold">{u.name || "—"}</p>
        <p className="text-sm text-muted-foreground">{u.email}</p>
        <div className="flex items-center gap-2 mt-2">
          <TierBadge tier={sub.tier} status={sub.status} />
          {u.email_verified ? <Badge className="bg-green-100 text-green-800">Verified</Badge> : <Badge className="bg-amber-100 text-amber-800">Unverified</Badge>}
          {u.role && <Badge variant="outline" className="capitalize">{u.role}</Badge>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <Info label="Joined" value={u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"} />
        <Info label="Auth" value={u.auth_provider || "email"} />
        <Info label="Trial days left" value={ent.trial_days_left ?? "—"} />
        <Info label="Locked" value={ent.locked ? "Yes" : "No"} />
        <Info label="Plan renews" value={sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : "—"} />
        <Info label="Trial end" value={sub.trial_end ? new Date(sub.trial_end).toLocaleDateString() : "—"} />
      </div>

      <div className="rounded-lg border border-zinc-200 p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Usage today</p>
        <div className="flex gap-4 text-sm">
          <span>Tailor: <strong>{d.usage_today?.tailor ?? 0}</strong>/{ent.caps?.tailor ?? "—"}</span>
          <span>Cover: <strong>{d.usage_today?.cover ?? 0}</strong>/{ent.caps?.cover ?? "—"}</span>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Attribution</p>
        <div className="text-sm space-y-1">
          <p>Affiliate: <strong>{d.attribution?.affiliate_code || "—"}</strong></p>
          <p>Referred by user: <strong className="font-mono text-xs">{d.attribution?.referred_by_user || "—"}</strong></p>
          <p>Tier-2 source: <strong>{d.attribution?.tier2_source_affiliate || "—"}</strong></p>
        </div>
      </div>

      <div className="border-t border-zinc-200 pt-4 space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actions</p>

        <div className="flex items-end gap-2">
          <div className="flex-1"><Label className="text-xs">Grant trial days</Label>
            <Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} data-testid="grant-days-input" /></div>
          <Button disabled={busy} onClick={() => act(() => api.post(`/admin/users/${userId}/grant-trial`, { days: Number(days) }), "Trial extended.")} data-testid="grant-trial-btn">Grant</Button>
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1"><Label className="text-xs">Comp subscription</Label>
            <Select value={compTier} onValueChange={setCompTier}>
              <SelectTrigger data-testid="comp-tier-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="lite">Lite</SelectItem><SelectItem value="pro">Pro</SelectItem></SelectContent>
            </Select></div>
          <div className="w-20"><Label className="text-xs">Months</Label>
            <Input type="number" min={1} max={36} value={compMonths} onChange={(e) => setCompMonths(e.target.value)} data-testid="comp-months-input" /></div>
          <Button variant="secondary" disabled={busy} onClick={() => act(() => api.post(`/admin/users/${userId}/comp`, { tier: compTier, months: Number(compMonths) }), "Subscription comped.")} data-testid="comp-btn">Comp</Button>
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1"><Label className="text-xs">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">User</SelectItem>
                <SelectItem value="support">Support</SelectItem>
                {isSuper && <SelectItem value="admin">Admin</SelectItem>}
              </SelectContent>
            </Select></div>
          <Button variant="secondary" disabled={busy} onClick={() => act(() => api.post(`/admin/users/${userId}/set-role`, { role: role === "none" ? "" : role }), "Role updated.")} data-testid="set-role-btn">Set role</Button>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" disabled={busy} data-testid="cancel-sub-btn">Cancel subscription</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel this subscription?</AlertDialogTitle>
              <AlertDialogDescription>Sets the user back to trial status and stops any future affiliate commission accrual. Past commissions are unaffected.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep it</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => act(() => api.post(`/admin/users/${userId}/cancel-sub`), "Subscription canceled.")} data-testid="confirm-cancel-sub">Cancel subscription</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-lg border border-zinc-200 p-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-medium mt-0.5">{value}</p>
    </div>
  );
}

export default function AdminUsers() {
  const { user } = useAuth();
  const isSuper = !!user?.is_admin && !user?.role;
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: q.trim() ? { q: q.trim() } : {} });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load users."); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><Users className="h-7 w-7 text-[#24468C]" strokeWidth={1.75} /> Users</h1>
        <p className="text-muted-foreground text-sm">Search users, review their plan & usage, and manage access.</p>
      </div>

      <div className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by email or user ID" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="users-search-input" />
        </div>
        <Button onClick={load} data-testid="users-search-btn">Search</Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-[#24468C]" /></div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="users-empty">No users found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Plan</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Joined</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.user_id} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors" onClick={() => setSelected(u.user_id)} data-testid={`user-row-${u.user_id}`}>
                    <td className="px-4 py-3 font-medium">{u.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.name || "—"}</td>
                    <td className="px-4 py-3"><TierBadge tier={u.tier} status={u.status} /></td>
                    <td className="px-4 py-3 capitalize text-muted-foreground">{u.role || "user"}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="user-detail-sheet">
          <SheetHeader><SheetTitle>User detail</SheetTitle></SheetHeader>
          <div className="mt-4">{selected && <Detail userId={selected} onChanged={load} isSuper={isSuper} />}</div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
